// FILE: src/shared/services/dadata.ts
// VERSION: 1.0.0
// PURPOSE: Сервис интеграции с DaData API — поиск компаний по ИНН, банков по БИК.

// TYPES: Ответы DaData API
export interface DaDataCompany {
  value: string; // Краткое название
  data: {
    inn: string | null;
    kpp: string | null;
    ogrn: string | null;
    name: {
      full_with_opf: string | null;
      short_with_opf: string | null;
    };
    address: {
      value: string | null;
      unrestricted_value: string | null;
    };
    management?: {
      name: string | null;
      post: string | null;
    };
    type: 'LEGAL' | 'INDIVIDUAL';
    state: {
      status: string | null;
    };
    okved: string | null;
    okved_type: string | null;
    phones?: Array<{ value: string }>;
    emails?: Array<{ value: string }>;
  };
}

export interface DaDataBank {
  value: string; // Название банка
  data: {
    bic: string | null;
    swift: string | null;
    inn: string | null;
    kpp: string | null;
    name: {
      payment: string | null;
      full: string | null;
      short: string | null;
    };
    correspondent_account: string | null;
    address: {
      value: string | null;
    };
    state: {
      status: string | null;
    };
  };
}

const DADATA_API_KEY = process.env.NEXT_PUBLIC_DADATA_API_KEY || '';
const BASE_URL = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs';

// FUNC: Внутренний метод для запросов к DaData API
async function fetchDaData<T>(endpoint: string, body: Record<string, unknown>): Promise<T[]> {
  if (!DADATA_API_KEY) {
    return [];
  }

  try {
    const res = await fetch(`${BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Token ${DADATA_API_KEY}`,
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      console.error(`[DaData] Ошибка ${res.status}: ${res.statusText}`);
      return [];
    }

    const data = await res.json();
    return data.suggestions || [];
  } catch (err) {
    console.error('[DaData] Ошибка запроса:', err);
    return [];
  }
}

export const DaDataService = {
  // FUNC: Поиск компании по ИНН (точное совпадение)
  async findCompanyByInn(inn: string): Promise<DaDataCompany[]> {
    return fetchDaData<DaDataCompany>('findById/party', { query: inn, count: 5 });
  },

  // FUNC: Подсказки по компаниям (по названию, ИНН, ОГРН)
  async suggestCompany(query: string): Promise<DaDataCompany[]> {
    return fetchDaData<DaDataCompany>('suggest/party', { query, count: 5 });
  },

  // FUNC: Поиск банка по БИК (точное совпадение)
  async findBankByBik(bik: string): Promise<DaDataBank[]> {
    return fetchDaData<DaDataBank>('findById/bank', { query: bik, count: 5 });
  },

  // FUNC: Подсказки по банкам (по названию, БИК, SWIFT)
  async suggestBank(query: string): Promise<DaDataBank[]> {
    return fetchDaData<DaDataBank>('suggest/bank', { query, count: 5 });
  },
};
